/**
 * cart.ts — корзина: localStorage + серверная синхронизация + анимации
 * Проект: TorgiRT (shared — все витрины)
 */

import type { Product, CartItem } from './types';
import { state, CART_STORAGE_KEY, UI_TIMING, refs, productLookup } from './state';
import { getConfig, isCartAllowedPage, isOnZakazPage, scrollBehavior } from './helpers';
import {
  renderCartSummary, renderCartItemsGrid, renderCartItemsList,
  renderCartBadge, renderCartItems, renderSyncIndicator,
} from './render';
import { updateFloatingCartText, updateFloatingCartVisibility } from './floating-cart';
import { showFlash } from './toast';

export function findProduct(article: string): Product | CartItem | null {
  const fromState = state.products.find((product) => product.article === article);
  if (fromState) {
    return fromState;
  }
  const fromLookup = productLookup.get(article);
  if (fromLookup) {
    return fromLookup;
  }
  const fromCart = state.cart.find((item) => item.article === article);
  if (fromCart) {
    return fromCart;
  }
  return null;
}

/* ── Корзина: localStorage + серверная синхронизация ── */

export function loadCartFromStorage(): void {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        state.cart = parsed;
      }
    }
  } catch {
    state.cart = [];
  }
}

/** Debounce + single-flight: один in-flight POST, отмена pending debounce при immediate sync */
let syncTimer: ReturnType<typeof setTimeout> | null = null;
let syncInFlight: Promise<Response | void> | null = null;
/** Инкремент при каждой мутации корзины — go-to-zakaz не ждёт stale in-flight после add/qty */
let syncGeneration = 0;
let syncInFlightGeneration = 0;

export function saveCartToStorage(): void {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state.cart));
    syncGeneration++;
  } catch {
    // localStorage переполнен — игнорируем
  }
}

/**
 * Синхронизация корзины с сервером (отправляет текущее состояние).
 * Cancel debounce + single-flight — иначе go-to-zakaz + scheduleSync дают параллельные POST → cart_persist_verify_failed.
 */
export function syncCartToServer(): Promise<Response | void> {
  if (syncTimer !== null) {
    clearTimeout(syncTimer);
    syncTimer = null;
  }
  if (syncInFlight && syncInFlightGeneration === syncGeneration) {
    return syncInFlight;
  }

  const syncUrl = getConfig("ajaxCartSyncUrl", "/i/ajax/cart/sync") as string;
  const csrfToken = getConfig("csrfToken", "") as string;
  const capturedGeneration = syncGeneration;
  syncInFlightGeneration = capturedGeneration;

  const cartData = state.cart.map((item) => ({
    article: item.article,
    name: item.name,
    price: item.price,
    unit: item.unit,
    quantity: item.quantity,
  }));

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  };
  if (csrfToken) {
    headers["X-CSRF-Token"] = csrfToken;
  }

  syncInFlight = fetch(syncUrl, {
    method: "POST",
    credentials: "same-origin",
    headers,
    body: JSON.stringify({ cart: cartData, csrf_token: csrfToken }),
  })
    .then((res) => {
      const failed = !(res && res.ok);
      if (!state.syncFailed && failed) {
        showFlash('Не удалось сохранить корзину. Проверьте соединение.', 'error');
      }
      state.syncFailed = failed;
      renderSyncIndicator();
      return res;
    })
    .catch(() => {
      if (!state.syncFailed) {
        showFlash('Не удалось сохранить корзину. Проверьте соединение.', 'error');
      }
      state.syncFailed = true;
      renderSyncIndicator();
    })
    .finally(() => {
      if (syncInFlightGeneration === capturedGeneration) {
        syncInFlight = null;
      }
    });

  return syncInFlight;
}

export function scheduleSyncToServer(): void {
  if (syncTimer !== null) clearTimeout(syncTimer);
  const delay = getConfig("scheduleDelay", 400) as number;
  syncTimer = setTimeout(() => {
    syncTimer = null;
    void syncCartToServer();
  }, delay);
}

export function addToCart(article: string): void {
  const product = findProduct(article);
  if (!product) {
    return;
  }

  const stock = (product as Product).stock ?? 0;
  if (stock <= 0) {
    return;
  }

  const existing = state.cart.find((item) => item.article === article);
  const currentQty = existing ? existing.quantity : 0;

  if (currentQty >= stock) {
    return;
  }

  if (existing) {
    existing.quantity += 1;
  } else {
    state.cart.push({ ...product, quantity: 1 } as CartItem);
  }

  saveCartToStorage();
  scheduleSyncToServer();
}

let scrollCompensationGeneration = 0;

function cartBlocksHeight(): number {
  let h = 0;
  const summary = document.getElementById('cart-summary');
  const wrap = document.getElementById('cart-items-wrap');
  if (summary) h += summary.offsetHeight;
  if (wrap) h += wrap.offsetHeight;
  return h;
}

function maxDocumentScrollY(): number {
  return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
}

/**
 * Компенсация скролла при изменении высоты #cart-summary и #cart-items-wrap.
 */
export function withScrollCompensation(fn: () => void): void {
  if (!isCartAllowedPage() || document.body.style.position === 'fixed') {
    fn();
    return;
  }

  const scrollBefore = window.scrollY;
  const heightBefore = cartBlocksHeight();

  fn();

  const growth = cartBlocksHeight() - heightBefore;
  const target = Math.min(Math.max(0, scrollBefore + growth), maxDocumentScrollY());
  if (Math.abs(window.scrollY - target) <= 2) {
    return;
  }

  const gen = ++scrollCompensationGeneration;
  const applyTarget = (): void => {
    if (gen !== scrollCompensationGeneration) return;
    window.scrollTo(0, target);
  };

  applyTarget();
  requestAnimationFrame(applyTarget);
}

/* ── Animated cart toggle ── */
export function toggleCartAnimated(): void {
  if (!refs.cartItemsWrap || !refs.cartItems) return;

  if (isOnZakazPage()) {
    document.getElementById("zakaz-cart-anchor")?.scrollIntoView({ behavior: scrollBehavior() });
    return;
  }

  if (state.cartOpen) {
    state.cartOpen = false;
    refs.cartItemsWrap.style.maxHeight = "";
    refs.cartItemsWrap.classList.add("hidden");
    refs.cartItemsWrap.classList.remove("is-cart-accent", "is-opening");
    refs.cartItems.innerHTML = "";
    renderCartSummary();
    updateFloatingCartText();
    updateFloatingCartVisibility();
    return;
  }

  if (state.cart.length === 0) return;

  state.cartOpen = true;

  refs.cartItems.innerHTML =
    state.viewMode === "grid" ? renderCartItemsGrid() : renderCartItemsList();
  refs.cartItemsWrap.classList.add("is-opening");
  refs.cartItemsWrap.classList.remove("hidden");
  renderCartSummary();
  updateFloatingCartText();
  updateFloatingCartVisibility();

  function animateIn(): void {
    const fullHeight = refs.cartItemsWrap!.scrollHeight;
    void refs.cartItemsWrap!.offsetHeight;
    refs.cartItemsWrap!.style.maxHeight = fullHeight + "px";
    refs.cartItemsWrap!.classList.remove("is-opening");

    function onDone(): void {
      refs.cartItemsWrap!.style.maxHeight = "";
      refs.cartItemsWrap!.removeEventListener("transitionend", onDone);
    }
    refs.cartItemsWrap!.addEventListener("transitionend", onDone);

    refs.cartItemsWrap!.classList.add("is-cart-accent");
    setTimeout(function () {
      if (refs.cartItemsWrap) refs.cartItemsWrap.classList.remove("is-cart-accent");
    }, UI_TIMING.accent);
  }

  const rect = refs.cartSummary!.getBoundingClientRect();
  const needsScroll = rect.bottom < 0 || rect.top > window.innerHeight * 0.5;

  if (needsScroll) {
    window.scrollTo({ top: 0, behavior: scrollBehavior() });
    setTimeout(animateIn, UI_TIMING.scrollSettle);
  } else {
    requestAnimationFrame(function () {
      requestAnimationFrame(animateIn);
    });
  }
}

/* ── Floating bar click ── */
export function scrollToCartArea(): void {
  if (isOnZakazPage()) {
    const anchor = document.getElementById("zakaz-cart-anchor");
    if (anchor) {
      anchor.scrollIntoView({ behavior: scrollBehavior() });
      setTimeout(function () {
        anchor.classList.remove("is-cart-hint");
        void anchor.offsetWidth;
        anchor.classList.add("is-cart-hint");
        setTimeout(function () {
          anchor.classList.remove("is-cart-hint");
        }, UI_TIMING.hintCleanup);
      }, UI_TIMING.scrollSettle);
    }
    return;
  }

  window.scrollTo({ top: 0, behavior: scrollBehavior() });
  setTimeout(function () {
    const target = document.querySelector('[data-action="toggle-cart-summary"]')
               || document.querySelector(".cart-button");
    if (!target) return;
    target.classList.remove("is-cart-hint");
    void (target as HTMLElement).offsetWidth;
    target.classList.add("is-cart-hint");
    setTimeout(function () {
      target.classList.remove("is-cart-hint");
    }, UI_TIMING.hintCleanup);
  }, UI_TIMING.scrollSettle);
}

export function updateQuantity(article: string, delta: number): void {
  state.cart = state.cart
    .map((item) => {
      if (item.article !== article) {
        return item;
      }
      let newQty = item.quantity + delta;
      const stock = (item as any).stock ?? 0;
      if (stock > 0 && newQty > stock) {
        newQty = stock;
      }
      return { ...item, quantity: Math.max(0, newQty) };
    })
    .filter((item) => item.quantity > 0);

  if (state.cart.length === 0) {
    state.cartOpen = false;
  }

  saveCartToStorage();
  scheduleSyncToServer();
}

/* ── Очистка корзины ── */

export function clearCart(): void {
  state.cart = [];
  state.cartOpen = false;
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
  } catch {
    // Если localStorage недоступен — игнорируем
  }
  void syncCartToServer();
  renderCartBadge();
  renderCartSummary();
  renderCartItems();
  updateFloatingCartText();
  updateFloatingCartVisibility();
}
